import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router'
import { Button, Card, Checkbox, Form, Input, Tooltip, Typography } from 'antd'
import React from 'react'
import { z } from 'zod'
import { redirectPath, useAuth } from '../auth'
import { FormItem } from '../components/form-item'
import useForm from '../hooks/use-form'
import { useTranslation } from 'react-i18next'
import { AuthenticateInput } from '../user/input/authenticate-input'
import { useTitle } from '../hooks/use-title'

const { Text, Title } = Typography

export const Route = createFileRoute('/_guest/login')({
    validateSearch: z.object({
        redirect: z.string().optional().catch(''),
    }),
    component: LoginComponent,
})

function LoginComponent() {
    const { t } = useTranslation()
    const search = Route.useSearch()
    const router = useRouter()
    const navigate = useNavigate({ from: '/login' })
    const { login } = useAuth()

    const formProps = useForm({
        mutation: login,
        onSuccess: () => {
            // ??????????????????????????????????????
            setTimeout(() => {
                router.invalidate().finally(() => {
                    return navigate({ to: redirectPath(search.redirect) })
                })
            }, 0)
        },
    })

    const remember = Form.useWatch('remember', formProps.form)

    useTitle(t('Sign in'))

    return (
        <Card>
            <div className="mb-6">
                <Title level={2}>{t('Sign in')}</Title>
            </div>
            <Form
                name="basic"
                layout="vertical"
                style={{ minWidth: 300 }}
                initialValues={
                    import.meta.env.DEV
                        ? {
                              email: 'j4mie@hey.com',
                              password: 'secret secret',
                              remember: true,
                          }
                        : {
                              remember: true,
                          }
                }
                autoComplete="off"
                {...formProps}
            >
                <FormItem<AuthenticateInput>
                    label={t('Email')}
                    name="email"
                    required
                >
                    <Input type="email" />
                </FormItem>

                <FormItem<AuthenticateInput>
                    label={t('Password')}
                    name="password"
                    required
                >
                    <Input.Password />
                </FormItem>

                <FormItem>
                    <FormItem<AuthenticateInput>
                        name="remember"
                        valuePropName="checked"
                        noStyle
                    >
                        <Checkbox>
                            <Tooltip
                                title={
                                    remember
                                        ? t('You will be logged out after 14 days of inactivity')
                                        : t('You will be logged out after 1 hour of inactivity')
                                }
                            >
                                {t('Remember me')}
                            </Tooltip>
                        </Checkbox>
                    </FormItem>

                    <Link
                        className="float-right text-primary"
                        to="/forgot"
                    >
                        {t('Forgot password?')}
                    </Link>
                </FormItem>

                <FormItem className="mb-0">
                    <Button
                        loading={login.isPending}
                        block
                        type="primary"
                        htmlType="submit"
                        size="large"
                    >
                        {t('Sign in')}
                    </Button>
                    <div className="mt-5 justify-center flex space-x-1">
                        <Text>{t("Don't have an account?")}</Text>
                        <Link
                            className="text-primary"
                            to="/"
                            search={{ redirect: search.redirect }}
                        >
                            {t('Sign up now')}
                        </Link>
                    </div>
                </FormItem>
            </Form>
        </Card>
    )
}
